import { argbFromHex, Hct, hexFromArgb } from "@material/material-color-utilities";

import type { Priority } from "../tasks/types.js";
import { distributedCategoricalColor } from "./palette.js";
import { DEFAULT_EVENT_COLOR, DEFAULT_PRIORITY_COLORS, type ThemeConfig } from "./types.js";

/** A soft, theme-harmonious fill for an entry (task OR event) with no topic and
 *  no calendar color: a low-chroma, mid-tone tint of the theme's source hue, so
 *  it reads as "neutral but on-theme" rather than a flat gray. The block wash
 *  still differentiates tasks (lighter) from events (more saturated). */
export function defaultEntryColor(theme: ThemeConfig): string {
  try {
    const hue = Hct.fromInt(argbFromHex(theme.source)).hue;
    return hexFromArgb(Hct.from(hue, 16, 58).toInt());
  } catch {
    return DEFAULT_EVENT_COLOR;
  }
}

/** Which scope of the specificity hierarchy supplied a resolved color. */
export type EntityScope = "custom" | "topic" | "calendar" | "priority" | "kind" | "global" | "auto" | "none";

export interface EntityColorTarget {
  custom_color?: string | null;
  is_event?: boolean;
  priority?: Priority;
}

/** Priority accent for the flag/wash — theme override → traffic-light default. */
export function priorityColor(theme: ThemeConfig, priority: Priority): string {
  return theme.entity.priority?.[priority] || DEFAULT_PRIORITY_COLORS[priority];
}

/** Per-kind color (task/event) → global → null. */
export function kindColor(theme: ThemeConfig, isEvent: boolean): string | null {
  const k = theme.entity.kind;
  return (isEvent ? k?.event : k?.task) ?? theme.entity.global ?? null;
}

/** Default fill for an event with no more-specific color. */
export function eventDefaultColor(theme: ThemeConfig): string {
  return theme.entity.kind?.event ?? theme.entity.global ?? defaultEntryColor(theme);
}

/** A topic's color: explicit override → distributed palette by stable index. */
export function topicColorResolved(theme: ThemeConfig, topicId: string, index: number): string {
  return theme.entity.topics?.[topicId] ?? distributedCategoricalColor(theme, index);
}

/** A calendar's color: explicit override → distributed palette by stable index.
 *  Callers pass CAL_PALETTE_OFFSET + i, so calendars and topics draw from
 *  disjoint positions of the SAME farthest-first sequence until it wraps. */
export function calendarColorResolved(theme: ThemeConfig, calendarId: string, index: number): string {
  return theme.entity.calendars?.[calendarId] ?? distributedCategoricalColor(theme, index);
}

/**
 * Full specificity walk for a single effective color, with provenance:
 * custom → topic(mix, passed in) → calendar(passed in) → priority → kind → global.
 * Callers resolve topic/calendar colors by id (they hold the ordered lists) and
 * pass them in; this owns the precedence so it stays in one place.
 */
export function resolveEntityColor(
  theme: ThemeConfig,
  target: EntityColorTarget,
  ctx: { topicColor?: string | null; calendarColor?: string | null } = {},
): { color: string | null; scope: EntityScope } {
  if (target.custom_color) return { color: target.custom_color, scope: "custom" };
  if (ctx.topicColor) return { color: ctx.topicColor, scope: "topic" };
  if (ctx.calendarColor) return { color: ctx.calendarColor, scope: "calendar" };
  if (target.priority && target.priority !== "none") {
    const p = priorityColor(theme, target.priority);
    if (p) return { color: p, scope: "priority" };
  }
  const k = kindColor(theme, !!target.is_event);
  if (k) return { color: k, scope: target.is_event || theme.entity.kind ? "kind" : "global" };
  if (target.is_event) return { color: eventDefaultColor(theme), scope: "kind" };
  return { color: null, scope: "none" };
}
